
import React, { useState, useRef, useContext, useEffect } from 'react';
import {
    Box, Typography, Grid, TextField, Button, Divider, IconButton,
    MenuItem, OutlinedInput, Select, FormControl, Chip,
    InputLabel, CircularProgress, FormControlLabel, Checkbox,
    Autocomplete
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Context from '../../context/Context';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { Trash, Trash2 } from 'lucide-react';
import apiClient from '../../utils/apiClient';

const documentTypes = ['Business License', 'Insurance Certificate', 'Registration'];

const AddEditShowroom = () => {
    const { services, getServices, packageServices, getPackageServices } = useContext(Context);
    const location = useLocation();
    const showroomId = location?.state?.showroomId || '';


    const navigate = useNavigate();

    const [formData, setForm] = useState({
        id: '',
        name: '',
        description: '',
        email: "",
        locationLat: '',
        locationLng: '',
        address: '',
        contact: '',
        registeredFrom: 'admin',
        brand: '',
        commissionPercentage: "",
        isActive: true,
        isDeleted: false,
        isVerified: false,
        isPopular: false,
        isOnline: false,
        isMobileVerified: false,
        images: [],
    });

    const [imagesPreview, setImagesPreview] = useState([]);
    const [selectedServices, setSelectedServices] = useState([]);
    const [selectedPackageServices, setSelectedPackageServices] = useState([])
    const [documents, setDocuments] = useState([]);
    const [validateErrors, setValidateErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const imagesInputRef = useRef(null);
    const [errors, setErrors] = useState('');


    const getShowroomById = async () => {
        try {
            setLoading(true)
            const response = await apiClient.get(`/showroom/showroom/showroomdetails/${showroomId}`);
            if (response?.data?.status === true) {
                const data = response?.data?.showroom
                setForm({
                    id: data.showRoomId || '',
                    name: data.name || '',
                    description: data.description || '',
                    locationLat: data.locationLat?.toString() || '',
                    locationLng: data.locationLng?.toString() || '',
                    address: data.address || '',
                    email: data.email || '',
                    contact: data.contact || '',
                    brand: data.brand || '',
                    isActive: data.isActive ?? false,
                    isDeleted: data.isDeleted ?? false,
                    isMobileVerified: data.isMobileVerified ?? false,
                    isOnline: data.isOnline ?? false,
                    isPopular: data.isPopular ?? false,
                    isVerified: data.isVerified ?? false,
                    commissionPercentage: data.commissionPercentage || "",
                    images: [],
                });

                if (data.skillServices && Array.isArray(data.skillServices)) {
                    setSelectedServices(data.skillServices.map(service => ({
                        id: service.id,
                        name: service?.service?.name,
                        serviceId: service.serviceId
                    })));

                }
                if (data.skillPackageServices && Array.isArray(data.skillPackageServices)) {
                    setSelectedPackageServices(data.skillPackageServices.map(service => ({
                        id: service.id,
                        name: service?.packageService?.name,
                        serviceId: service?.packageServiceId
                    })));

                }
                if (data.images && Array.isArray(data.images)) {
                    setImagesPreview(data.images.map(image => ({
                        url: image.imageUrl,
                        id: image.id
                    })));
                }
                if (data?.documents && Array.isArray(data.documents)) {
                    setDocuments(data.documents.map(doc => ({
                        name: doc.documentName,
                        url: doc.documentPath,
                        id: doc.id,

                    })));
                }
            }
        } catch (error) {
            setErrors(error?.response?.data?.message || ' Error Occured');
        }
        finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        getServices();
        getPackageServices()
        getShowroomById()
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        setValidateErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleServiceSelect = (event, newValue) => {
        setSelectedServices(newValue);
    };

    const handlePackageServiceSelect = (e, newValue) => {
        setSelectedPackageServices(newValue);

    };
    const handleImagesChange = (e) => {
        const files = Array.from(e.target.files);
        const newPreviews = files.map(file => ({
            file,
            url: URL.createObjectURL(file)
        }));

        setForm(prev => ({
            ...prev,
            images: [...prev.images, ...files]
        }));

        setImagesPreview(prev => [...prev, ...newPreviews]);
    };

    const handleRemoveImage = (index) => {
        const updatedImages = [...formData.images];
        const updatedPreviews = [...imagesPreview];

        if (updatedPreviews[index]?.url?.startsWith('blob:')) {
            URL.revokeObjectURL(updatedPreviews[index].url);
        }

        updatedImages.splice(index, 1);
        updatedPreviews.splice(index, 1);

        setForm(prev => ({ ...prev, images: updatedImages }));
        setImagesPreview(updatedPreviews);
    };


    const validateForm = () => {
        const errors = {};

        if (!formData.name.trim()) errors.name = "Name is required.";
        const lat = parseFloat(formData.locationLat);
        if (!formData.locationLat.trim()) errors.locationLat = "Latitude is required.";
        else if (isNaN(lat) || lat < -90 || lat > 90) errors.locationLat = "Latitude must be between -90 and 90.";
        const lng = parseFloat(formData.locationLng);
        if (!formData.locationLng.trim()) errors.locationLng = "Longitude is required.";
        else if (isNaN(lng) || lng < -180 || lng > 180) errors.locationLng = "Longitude must be between -180 and 180.";
        if (!formData.address.trim()) errors.address = "Address is required.";
        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Invalid email format';
        }
        if (!formData.contact.trim()) {
            errors.contact = "Contact is required.";
        } else if (!/^[\d\s+()-]{6,}$/.test(formData.contact) && !/\S+@\S+\.\S+/.test(formData.contact)) {
            errors.contact = "Enter a valid phone number or email.";
        }

        if (!formData.commissionPercentage) errors.commissionPercentage = "Commission Percentage is reqiured"
        if (selectedServices.length === 0) errors.services = "Select at least one service.";
        if (selectedPackageServices.length === 0) errors.packageServices = "Select at least one package service.";

        if (formData.images.length === 0 && imagesPreview.length < 0) errors.images = "At least one showroom image is required.";

        return errors;
    };

    const handleDocumentNameChange = (index, name) => {
        const updated = [...documents];
        updated[index].name = name;
        setDocuments(updated);
    };

    const handleRemoveImages = async (id) => {
        try {
            const response = await apiClient.delete(`/showroomimage/image/${id}`)
            if (response.data.status === true) {
                setImagesPreview(imagesPreview.filter((image, index) => image.id !== id))
                setForm(prev => ({ ...prev, images: prev.images.filter((image, index) => image.id !== id) }))
                toast.success(response?.data?.message)
            }

        } catch (error) {
            setErrors(error?.response?.data?.message)
        }
    }

    const handleDocumentdelete = async (id) => {
        try {
            const response = await apiClient.delete(`/showroomdoucment/document/${id}`)
            if (response?.data?.status === true) {
                setDocuments(documents.filter(item => item.id !== id))
                toast.success(response?.data?.message || "Document deleted")
            }
        } catch (error) {
            setErrors(error?.response?.data?.message)
        }
    }

    const handleDocumentFileChange = (index, file) => {
        const preview = file && file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
        const updated = [...documents];
        updated[index] = { ...updated[index], file, preview };
        setDocuments(updated);
    };

    const removeDocument = (index) => {
        const updated = [...documents];
        if (updated[index].preview) URL.revokeObjectURL(updated[index].preview);
        updated.splice(index, 1);
        setDocuments(updated);
    };

    const addNewDocument = () => {
        setDocuments(prev => [...prev, { name: '', file: null, preview: null }]);
    };


    const handleServiceDelete = async (serviceToDelete) => {
        if (!serviceToDelete.id) {
            // No backend id, just remove locally
            setSelectedServices(prev => prev.filter(s => s.serviceId !== serviceToDelete.serviceId));
            return;
        }

        try {
            setLoading(true);
            const response = await apiClient.delete(`/skills/deleteservice/${serviceToDelete.id}`);
            if (response?.data?.status === true) {
                toast.success(response?.data?.message || "Service deleted successfully");
                setSelectedServices(prev => prev.filter(s => s.serviceId !== serviceToDelete.serviceId));
            } else {
                setErrors(response?.data?.message || "Failed to delete service");
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || "Error deleting service");
        } finally {
            setLoading(false);
        }
    };

    const handlePackageServiceDelete = async (serviceToDelete) => {
        if (!serviceToDelete.id) {
            // No backend id, just remove locally
            setSelectedPackageServices(prev => prev.filter(s => s.serviceId !== serviceToDelete.serviceId));
            return;
        }

        try {
            setLoading(true);
            const response = await apiClient.delete(`/skills/deletepackageservice/${serviceToDelete.id}`);
            if (response?.data?.status === true) {
                toast.success(response?.data?.message || "Service deleted successfully");
                setSelectedPackageServices(prev => prev.filter(s => s.serviceId !== serviceToDelete.serviceId));
            } else {
                setErrors(response?.data?.message || "Failed to delete service");
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || "Error deleting service");
        } finally {
            setLoading(false);
        }
    };

    const options = services.map(service => {

        const existing = selectedServices?.find(s => s.serviceId === service.id);
        return existing ? existing : { id: null, serviceId: service.id, name: service.name };
    });
    const packageServicesoptions = packageServices.map(service => {

        const existing = selectedPackageServices?.find(s => s.serviceId === service.id);
        return existing ? existing : { id: null, serviceId: service.id, name: service.name };
    });


    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setValidateErrors(errors);
            return;
        }

        setLoading(true);
        try {
            const payload = new FormData();
            Object.entries(formData).forEach(([key, value]) => {
                if (typeof value === 'boolean' || typeof value === 'string') {
                    payload.append(key, value);
                }
            });

            const servicesJson = JSON.stringify(selectedServices.map(id => ({ id: id.serviceId })));
            const packageServicesJson = JSON.stringify(selectedPackageServices.map(id => ({ id: id.serviceId })));

            payload.append('services', servicesJson);
            payload.append('packageService', packageServicesJson);


            formData.images.forEach(imageFile => {
                payload.append('showRoomImages', imageFile);
            });

            documents.forEach((doc, index) => {
                if (doc.name && doc.file) {
                    payload.append('documentName', doc.name);
                    payload.append('showRoomDocuments', doc.file);
                }
            });
            let response;

const config = {
  headers: {
    "Content-Type": "multipart/form-data"
  }
};

if (showroomId) {
  response = await apiClient.put(
    `/showroom/showroom/${showroomId}`,
    payload,
    config
  );
} else {
  response = await apiClient.post(
    '/showroom/create',
    payload,
    config
  );
}
            if (response?.data?.status === true) {
                toast.success(response?.data?.message);
                navigate(-1);
            }
        } catch (error) {
            setErrors(error?.response?.data?.message || "Error")
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h4" mb={2}>
                {showroomId ? `Edit Service Center - ${formData.name}` : 'Add New Service Center'}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
                <Grid item size={{ xs: 12, sm: 6, md: 6 }}><TextField fullWidth label="Name" size="small" name="name" value={formData.name} onChange={handleChange} error={!!validateErrors.name} helperText={validateErrors.name} /></Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 6 }}><TextField fullWidth label="Description" size="small" name="description" value={formData.description} onChange={handleChange} /></Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 6 }}><TextField fullWidth label="Contact" size="small" name="contact" value={formData.contact} onChange={handleChange} error={!!validateErrors.contact} helperText={validateErrors.contact} /></Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 6 }}><TextField fullWidth label="Brand" size="small" name="brand" value={formData.brand} onChange={handleChange} error={!!validateErrors.brand} helperText={validateErrors.brand} /></Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                    <TextField fullWidth label="Email" size='small' name="email" value={formData.email} onChange={handleChange} error={!!validateErrors.email} helperText={validateErrors.email} />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 6 }}><TextField fullWidth label="Address" size="small" name="address" value={formData.address} onChange={handleChange} error={!!validateErrors.address} helperText={validateErrors.address} /></Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 6 }}><TextField fullWidth label="commission Percentage" size="small" name="commissionPercentage" value={formData.commissionPercentage} onChange={handleChange} error={!!validateErrors.commissionPercentage} helperText={validateErrors.commissionPercentage} /></Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 6 }}><TextField fullWidth label="Latitude" size="small" name="locationLat" value={formData.locationLat} onChange={handleChange} error={!!validateErrors.locationLat} helperText={validateErrors.locationLat} /></Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 6 }}><TextField fullWidth label="Longitude" size="small" name="locationLng" value={formData.locationLng} onChange={handleChange} error={!!validateErrors.locationLng} helperText={validateErrors.locationLng} /></Grid>
                <Typography variant="body2">
                    You should enter latitude for store listing. You can fetch location coordinates from{' '}
                    <a href="https://www.latlong.net" target="_blank" rel="noopener noreferrer">
                        https://www.latlong.net
                    </a>.
                </Typography>
                <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                    <Autocomplete
                        size='small'
                        multiple
                        options={options}
                        getOptionLabel={(option) => option.name}
                        value={selectedServices}
                        onChange={handleServiceSelect}
                        filterSelectedOptions
                        disableCloseOnSelect
                        isOptionEqualToValue={(option, value) => option.serviceId === value.serviceId}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    label={option.name}
                                    {...getTagProps({ index })}
                                    key={option.serviceId}
                                    onDelete={() => handleServiceDelete(option)}
                                    deleteIcon={<Trash2 size={16} />}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label="Select Services"
                                placeholder="Select multiple services"
                            />
                        )}
                    />
                    {validateErrors.services && <Typography color="error" variant="caption">{validateErrors.services}</Typography>}
                </Grid>

                <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                    <Autocomplete
                        size='small'
                        multiple
                        options={packageServicesoptions}
                        getOptionLabel={(option) => option.name}
                        value={selectedPackageServices}
                        onChange={handlePackageServiceSelect}
                        filterSelectedOptions
                        disableCloseOnSelect
                        isOptionEqualToValue={(option, value) => option.serviceId === value.serviceId}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    label={option.name}
                                    {...getTagProps({ index })}
                                    key={option.serviceId}
                                    onDelete={() => handlePackageServiceDelete(option)}
                                    deleteIcon={<Trash2 size={16} />}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label="Select Package Services"
                                placeholder="Select multiple services"
                            />
                        )}
                    />
                    {validateErrors.packageServices && <Typography color="error" variant="caption">{validateErrors.packageServices}</Typography>}
                </Grid>

                <Grid item size={12}>
                    <FormControlLabel control={<Checkbox checked={formData.isActive} onChange={handleChange} name="isActive" />} label="Active" />
                    <FormControlLabel control={<Checkbox checked={formData.isDeleted} onChange={handleChange} name="isDeleted" />} label="Deleted" />
                    <FormControlLabel control={<Checkbox checked={formData.isPopular} onChange={handleChange} name="isPopular" />} label="Popular" />
                    <FormControlLabel control={<Checkbox checked={formData.isMobileVerified} onChange={handleChange} name="isMobileVerified" />} label="Mobile Verified" />
                    <FormControlLabel control={<Checkbox checked={formData.isOnline} onChange={handleChange} name="isOnline" />} label="Online" />
                    <FormControlLabel control={<Checkbox checked={formData.isVerified} onChange={handleChange} name="isVerified" />} label="Verified" />
                </Grid>

                <Grid item size={12}>
                    <input type="file" multiple accept="image/*" ref={imagesInputRef} style={{ display: 'none' }} onChange={handleImagesChange} />
                    <Button variant="outlined" onClick={() => imagesInputRef.current.click()}>Upload Images</Button>
                    {validateErrors.images && <Typography color="error">{validateErrors.images}</Typography>}
                    <Box mt={2} display="flex" gap={2} flexWrap="wrap">
                        {imagesPreview.map((image, i) => (
                            <Box key={i} sx={{ position: 'relative', width: 100, height: 100 }}>
                                <img src={image.url} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4, border: "1px solid gray" }} />
                                {image.id ? (
                                    <IconButton
                                        onClick={() => handleRemoveImages(image.id)}
                                        sx={{ position: 'absolute', top: -10, right: -10, background: '#fff', ":hover": { background: '#FFFFFF' } }}
                                    >
                                        <Trash2 color='red' />
                                    </IconButton>
                                ) : (
                                    <IconButton
                                        onClick={() => handleRemoveImage(i)}
                                        sx={{ position: 'absolute', top: -10, right: -10, background: '#fff' }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                )}

                            </Box>
                        ))}

                    </Box>
                </Grid>

                <Grid item size={12}>
                    <Typography variant="subtitle1">Upload Documents</Typography>
                    {documents.map((doc, i) => (
                        <Grid container spacing={1} key={i} alignItems="center" mb={1} mt={2}>
                            <Grid item size={5}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Document Name</InputLabel>
                                    <Select value={doc.name} onChange={(e) => handleDocumentNameChange(i, e.target.value)} label="Document Name">
                                        {documentTypes.map((type, idx) => (
                                            <MenuItem key={idx} value={type}>{type}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item size={5}>
                                <Button variant="outlined" component="label" fullWidth>
                                    {doc.file ? doc.file.name : "Upload File"}
                                    <input type="file" accept="application/pdf,image/*" hidden onChange={(e) => handleDocumentFileChange(i, e.target.files[0])} />
                                </Button>
                            </Grid>
                            {doc.id ? (<Grid item size={2}>
                                <IconButton onClick={() => handleDocumentdelete(doc.id)}><Trash2 color='red' /></IconButton>
                            </Grid>) : (<Grid item size={2}>
                                <IconButton onClick={() => removeDocument(i)}><CloseIcon /></IconButton>
                            </Grid>)

                            }
                            <Grid item size={12}>
                                {(doc.file || doc.url) && (
                                    <>
                                        {doc.file ? (
                                            doc.file.type === 'application/pdf' ? (
                                                <Typography variant="body2" mt={1}>
                                                    PDF: <a href={URL.createObjectURL(doc.file)} target="_blank" rel="noreferrer">Open PDF</a>
                                                </Typography>
                                            ) : (
                                                <Box mt={1}>
                                                    <img src={URL.createObjectURL(doc.file)} alt="Document" style={{ width: 120, height: 120, objectFit: 'contain', border: '1px solid #ccc', borderRadius: 4 }} />
                                                </Box>
                                            )
                                        ) : doc.url?.endsWith('.pdf') ? (
                                            <Typography variant="body2" mt={1}>
                                                PDF: <a href={doc.url} target="_blank" rel="noreferrer">Open PDF</a>
                                            </Typography>
                                        ) : (
                                            <Box mt={1}>
                                                <img src={doc.url} alt="Document" style={{ width: 120, height: 120, objectFit: 'contain', border: '1px solid #ccc', borderRadius: 4 }} />
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Grid>


                        </Grid>
                    ))}
                    <Button variant="outlined" onClick={addNewDocument}>Add Document</Button>
                </Grid>
                {errors && (
                    <Grid item size={12}>
                        <Typography color="error" variant='body2'>{errors}</Typography>
                    </Grid>
                )}

                <Grid item size={12}>
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button variant="outlined" onClick={() => navigate(-1)}>Cancel</Button>
                        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
                            {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : showroomId ? "Update" : "Create"}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AddEditShowroom;

